import { Button, Card, Flex, Form } from "antd";
import { showConfirmModal } from "components/modals/confirmModal";
import { DeactivateStaffModal } from "components/modals/DeactivateStaffModal.component";
import { useSetStaffInstructorRole } from "hooks/mutations/user";
import { ToggleRow, optionBlockStyle } from "pages/settings/settingsRows";
import { useEffect, useState } from "react";
import { translate } from "shared/languages";
import { User } from "shared/types";
import { UserType } from "shared/types/enum";
import { hasRole } from "shared/utils/role";

// The studio Manager's own instructor on/off switch for one of their admin
// accounts, on the Staf detail page. Grant and revoke both live here (an
// admin already sees everything the instructor app shows, and revoking only
// takes power away), while the admin TIER stays with Spott (StaffRolesCard,
// system admin only). Shown for a Manager viewing any admin-tier account of
// their studio, peer Managers and their own account included (Staf row or
// Profil Aku); the server refuses other businesses and plain Admins. The
// purge behind it is instructor-surface only, so nobody is signed out of the
// dashboard, the Manager themselves included.
const InstructorRoleToggleCard = ({ user }: { user: User }) => {
  const [form] = Form.useForm<{ instructor: boolean }>();
  const initialInstructor = hasRole(user.roles, UserType.Instructor);
  const instructor = Form.useWatch("instructor", form) ?? initialInstructor;
  const { mutate, isPending } = useSetStaffInstructorRole();
  // Taking the role away opens the same pop-up as a deactivation: refused
  // while they still teach an upcoming class, which it lists with the
  // one-click removal; otherwise the one-line confirm.
  const [revokeOpen, setRevokeOpen] = useState(false);

  useEffect(() => {
    form.setFieldsValue({ instructor: initialInstructor });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const dirty = instructor !== initialInstructor;

  // The confirm names the direction in both its title and its one sentence, so
  // the switch's new position is restated rather than assumed (Andrew,
  // 2026-09-17). No session note: the purge behind this is instructor-surface
  // only, so nothing the person is looking at right now ends.
  const onSave = () => {
    if (!instructor) {
      setRevokeOpen(true);
      return;
    }
    showConfirmModal({
      tone: "amber",
      title: translate("staff_roles.confirm_grant_title"),
      content: translate("staff_roles.confirm_grant_body"),
      confirmLabel: translate("button.confirm"),
      onConfirm: () => mutate({ userId: user.id, instructor }),
    });
  };

  return (
    <Card>
      <DeactivateStaffModal
        open={revokeOpen}
        user={user}
        mode="revoke_role"
        pending={isPending}
        onConfirm={(removeUpcoming, onRefused) =>
          mutate(
            { userId: user.id, instructor: false, removeUpcoming },
            { onSuccess: () => setRevokeOpen(false), onError: onRefused }
          )
        }
        onClose={() => setRevokeOpen(false)}
      />
      <Flex vertical gap={20}>
        {/* No section title: the toggle row names itself, and a "Peran Staf"
            heading above a single instructor switch said less than the row
            under it (Andrew, 2026-09-17). */}
        <Form form={form} layout="vertical" initialValues={{ instructor: initialInstructor }}>
          <div style={optionBlockStyle(true)}>
            <ToggleRow
              name="instructor"
              labelKey="staff_roles.instructor_role_label"
              descKey="staff_roles.instructor_role_desc"
            />
          </div>
          <Flex style={{ marginTop: 18 }}>
            <Button type="primary" disabled={!dirty || isPending} loading={isPending} onClick={onSave}>
              {translate("button.save_changes")}
            </Button>
          </Flex>
        </Form>
      </Flex>
    </Card>
  );
};

export default InstructorRoleToggleCard;
